import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Col, Input, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import BtnCustom from "components/common/btn-custom";
import { ErrorToaster, successToaster } from "components/common/toast";
import {
  DeleteAIKeyAPI,
  GetAIKeyAPI,
  SaveAIKeyAPI,
  TestAIKeyAPI,
} from "network/user";

const SOURCE_KEYS = {
  database: "aiKey.sourceDatabase",
  server: "aiKey.sourceServer",
};

const errorText = (error, fallback) => error?.message || fallback;

// Chiave dell'AI (lettura carta da PDF, bozze vini, suggerimenti categorie).
// Si scrive solo qui: dopo il salvataggio si vede soltanto in forma mascherata.
const AIKeyCard = () => {
  const { t } = useTranslation("account");
  const [state, setState] = useState(null);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const response = await GetAIKeyAPI();
      setState(response?.data || null);
    } catch (error) {
      ErrorToaster(errorText(error, t("aiKey.loadError")));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (name, action, done) => {
    setBusy(name);
    try {
      const response = await action();
      done(response);
    } catch (error) {
      ErrorToaster(errorText(error, t("aiKey.failed")));
    } finally {
      setBusy("");
    }
  };

  const save = () =>
    run(
      "save",
      () => SaveAIKeyAPI(value),
      (response) => {
        setState(response?.data || null);
        setValue("");
        successToaster(t("aiKey.saved"));
      }
    );

  const remove = () =>
    run(
      "remove",
      DeleteAIKeyAPI,
      (response) => {
        setState(response?.data || null);
        successToaster(t("aiKey.removed"));
      }
    );

  const test = () =>
    run(
      "test",
      TestAIKeyAPI,
      (response) => {
        if (response?.success) successToaster(t("aiKey.works"));
        else ErrorToaster(response?.message || t("aiKey.notWorking"));
      }
    );

  return (
    <Row>
      <Col lg="12">
        <Card className="p-3">
          <CardHeader>
            <h3 className="title">{t("aiKey.title")}</h3>
          </CardHeader>
          <CardBody>
            <p>
              {state?.configured ? (
                <>
                  {t("aiKey.status")} <strong>{t("aiKey.active")}</strong> ({state.masked}),{" "}
                  {SOURCE_KEYS[state.source] ? t(SOURCE_KEYS[state.source]) : ""}.
                </>
              ) : (
                <>
                  {t("aiKey.status")} <strong>{t("aiKey.notConfigured")}</strong>{t("aiKey.notConfiguredText")}
                </>
              )}
            </p>
            <Row>
              <Col md="8">
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder={t("aiKey.placeholder")}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
                <small className="text-muted">
                  {t("aiKey.hint")}
                </small>
              </Col>
              <Col md="4" className="mt-2 mt-md-0">
                <BtnCustom
                  className="btn-fill"
                  color="primary"
                  type="button"
                  submittingText={t("aiKey.saving")}
                  isLoading={busy === "save"}
                  disabled={!value.trim() || !!busy}
                  onClick={save}
                >
                  {t("aiKey.save")}
                </BtnCustom>
              </Col>
            </Row>
            {state?.configured && (
              <div className="mt-3">
                <BtnCustom
                  color="info"
                  type="button"
                  className="mr-2"
                  submittingText={t("aiKey.testing")}
                  isLoading={busy === "test"}
                  disabled={!!busy}
                  onClick={test}
                >
                  {t("aiKey.test")}
                </BtnCustom>
                {state.source === "database" && (
                  <BtnCustom
                    color="danger"
                    type="button"
                    submittingText={t("aiKey.removing")}
                    isLoading={busy === "remove"}
                    disabled={!!busy}
                    onClick={remove}
                  >
                    {t("aiKey.remove")}
                  </BtnCustom>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default AIKeyCard;
